import type { ConstValues } from '../helpers.ts';
import type { DomainTargetingType } from './domains.ts';
import type { ComparisonMap, ComparisonType, CriteriaValue, Intersect } from './targetingValuesTypes.ts';

export const InventoryContentOption = {
  APP_BUNDLE_ID: 'app_bundle_id',
  INVENTORY: 'inventory',
  PAGE_URL: 'page_url',
  ADUNIT: 'adunit',
  ADUNIT_SIZE: 'adunit_size',
  PUBLISHER_ID: 'account',
  INSTANCE_HASH: 'instance',
  SITE: 'site',
  CONTENT_INTER_DIMENSION_OPERATOR: 'content_inter_dimension_operator',
  KEYWORDS: 'keywords',
  LINE_ITEM_APP_BUNDLE_ID: 'line_item_app_bundle_id',
  PUBLISHER_INVENTORY_RULES: 'publisher_inventory_rules',
  APP_INVENTORY: 'app_inventory',
  APP_GROUP: 'appgroup',
} as const;
export type InventoryContentOption = ConstValues<typeof InventoryContentOption>;

export type InventoryContentIdsTypeOption =
  | typeof InventoryContentOption.PUBLISHER_ID
  | typeof InventoryContentOption.INSTANCE_HASH
  | typeof InventoryContentOption.ADUNIT
  | typeof InventoryContentOption.SITE
  | typeof InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID
  | typeof InventoryContentOption.PUBLISHER_INVENTORY_RULES;

export const TargetableInventoryOption = {
  INCLUDES: 'includes',
  EXCLUDES: 'excludes',
  ID_BY_INSTANCES: '_idsByInstances',
} as const;
export type TargetableInventoryOption = ConstValues<typeof TargetableInventoryOption>;

/**
 * Type of App Inventory targeting - determines what kind of app identifier to use.
 * Used in App Inventory dropdown to select between App Name, App Bundle ID, and Inventory List targeting.
 */
export const AppInventoryType = {
  APP_NAME: 'app_name',
  APP_BUNDLE_ID: 'app_bundle_id',
  INVENTORY_LIST: 'inventory_list',
} as const;
export type AppInventoryType = ConstValues<typeof AppInventoryType>;

export interface InventoryAndContentApi {
  [InventoryContentOption.APP_BUNDLE_ID]?: {
    op: CriteriaValue;
    val?: {
      val: string;
      op: ComparisonType;
    }[];
    inventorylist_id?: string;
  } | null;
  [InventoryContentOption.PAGE_URL]?: {
    op: CriteriaValue;
    val: {
      val: string;
      op: ComparisonType;
    }[];
  } | null;
  [InventoryContentOption.ADUNIT]?: {
    op: Intersect;
    val: string;
  } | null;
  [InventoryContentOption.ADUNIT_SIZE]?: {
    op: Intersect;
    val: string;
  } | null;
  [TargetableInventoryOption.INCLUDES]?:
    | {
        [K in TargetableInventoryType]?: string | null;
      }
    | null;
  [TargetableInventoryOption.EXCLUDES]?:
    | {
        [K in TargetableInventoryType]?: string | null;
      }
    | null;
  [InventoryContentOption.PUBLISHER_ID]?: {
    op: Intersect;
    val: string;
  } | null;
  [InventoryContentOption.INSTANCE_HASH]?: {
    op: Intersect;
    val: string;
  } | null;
  [InventoryContentOption.SITE]?: {
    op: Intersect;
    val: string;
  } | null;
  [TargetableInventoryOption.ID_BY_INSTANCES]?: InstancesByIdObject;
  [InventoryContentOption.CONTENT_INTER_DIMENSION_OPERATOR]?: CriteriaValue;
  [InventoryContentOption.KEYWORDS]?: {
    op: Intersect;
    val: string[];
  } | null;
  [InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID]?: {
    op: Intersect;
    val: string;
  } | null;
  [InventoryContentOption.PUBLISHER_INVENTORY_RULES]?: PublisherInventoryRuleApi[] | null;
  [InventoryContentOption.APP_GROUP]?: {
    op: string;
    val: string;
  } | null;
}

export type InventoryAndContentApiRequest = InventoryAndContentApi & {
  [InventoryContentOption.APP_BUNDLE_ID]?: {
    op: CriteriaValue;
    val?: {
      val: string;
      op: ComparisonType;
    }[];
    inventorylist?: {
      id: string;
      type: 'allow';
    };
  };
};

type InstanceId = string;

interface IncludesExcludes {
  [TargetableInventoryOption.INCLUDES]?:
    | {
        [K in TargetableInventoryType]?: string | null;
      }
    | null;
  [TargetableInventoryOption.EXCLUDES]?:
    | {
        [K in TargetableInventoryType]?: string | null;
      }
    | null;
}

export type InstancesByIdObject = Record<InstanceId, IncludesExcludes> | null;

export interface InventoryAndContentState {
  [InventoryContentOption.ADUNIT]: IntersectOptionPayload;
  [InventoryContentOption.APP_BUNDLE_ID]: AppBundleIdOptionPayload;
  [InventoryContentOption.PAGE_URL]: InventoryAndContentOptionPayload;
  [InventoryContentOption.ADUNIT_SIZE]: IntersectOptionPayload;
  [InventoryContentOption.INVENTORY]: InventoryOptionPayload;
  [InventoryContentOption.PUBLISHER_ID]: IntersectOptionPayload;
  [InventoryContentOption.INSTANCE_HASH]: IntersectOptionPayload;
  [InventoryContentOption.SITE]: IntersectOptionPayload;
  [InventoryContentOption.CONTENT_INTER_DIMENSION_OPERATOR]: CriteriaValue | null;
  [InventoryContentOption.KEYWORDS]: IntersectOptionPayload;
  [InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID]: IntersectOptionPayload;
  [InventoryContentOption.PUBLISHER_INVENTORY_RULES]: PublisherInventoryRule[];
  [InventoryContentOption.APP_GROUP]: AppInventoryOptionPayload;
}

export type AppBundleIdOptionPayload = {
  op: CriteriaValue;
  val?: ComparisonMap;
  inventorylist_id?: string | null;
};

/**
 * AppGroup represents a group of apps with a friendly name.
 * Used for App Inventory targeting feature.
 * Data comes from optionsByPath query with path "deal.package.targeting.app_group".
 */
export interface AppGroup {
  id: string;
  name: string;
  object: string;
}

/**
 * Canonical, transport-agnostic shape of a single App Name search result, normalized from
 * either the GraphQL `appGroupSearch` query (oa-ui) or the REST `/appgroup/search` endpoint
 * (unity/REST apps). Used by shared App Inventory search/validation utilities.
 */
export interface AppGroupSearchEntry {
  match: string | null;
  suggestions: AppGroupSearchEntrySuggestion[];
}

export interface AppGroupSearchEntrySuggestion {
  id: string;
  name: string;
}

/**
 * Payload type for App Inventory targeting option.
 * Similar to AppBundleIdOptionPayload but without inventorylist support.
 */
export type AppInventoryOptionPayload = {
  op: CriteriaValue;
  val?: ComparisonMap;
};

export interface InventoryAndContentOptionPayload {
  op: CriteriaValue;
  val: ComparisonMap;
}

export interface IntersectOptionPayload {
  op: Intersect;
  val: string[];
}

export interface InventoryOptionPayload {
  [TargetableInventoryOption.INCLUDES]: TargetableInventoryList;
  [TargetableInventoryOption.EXCLUDES]: TargetableInventoryList;
}

export type InventoryOptionPayloadApiFormat = {
  [K in TargetableInventoryType]?: string | null;
};

export const TargetableInventoryType = {
  INSTANCE: 'instance',
  ADUNITGROUP: 'adunitgroup',
  ACCOUNT: 'account',
  SITE: 'site',
  ADUNIT: 'adunit',
  SITESECTION: 'sitesection',
  NETWORK: 'network',
} as const;
export type TargetableInventoryType = ConstValues<typeof TargetableInventoryType>;

export const TargetableInventoryParentSearch = {
  [TargetableInventoryType.ACCOUNT]: 'account_uid',
  [TargetableInventoryType.SITE]: 'site_uid',
};

export interface TargetableInventoryEntities {
  adunitgroup?: string | null;
  account?: string | null;
  site?: string | null;
  adunit?: string | null;
  sitesection?: string | null;
}

export interface TargetableInventory {
  uid: string;
  id: string;
  name: string;
  type: string;
  account_uid?: string | null;
  parent_name?: string | null;
  type_full?: string | null;
  instance_uid?: string;
}

export type TargetableInventoryList = TargetableInventory[];

export interface TargetableInventoryExcludes {
  grouped: Record<string, TargetableInventoryList>;
  other: TargetableInventoryList;
}
export type InstanceProps = {
  id: string;
  market_operator_id: string;
  name: string;
};
export interface InventoryTargetingContent<T> {
  includes: T;
  excludes: T;
}

export type ParentAccountFeatures = {
  isAdUnitTargetingEnabled: boolean;
  isSiteTargetingEnabled: boolean;
};

export const PublisherInventoryRuleActions = {
  BLOCK: 'block',
} as const;
export type PublisherInventoryRuleAction = ConstValues<typeof PublisherInventoryRuleActions>;

export const CustomBlockingRulesOptions = {
  BUNDLE_ID: 'bundle_id',
  DOMAIN: 'domain',
} as const;
export type CustomBlockingRulesOption = ConstValues<typeof CustomBlockingRulesOptions>;

export const CustomBlockingRulesSubdomain = 'subdomain';

export type PublisherInventoryRuleApi = {
  action: PublisherInventoryRuleAction;
  bundle_id: string[];
  domain: string[];
  publisher_id: string;
  subdomain: string[];
};

export type PublisherInventoryRule = PublisherInventoryRuleApi & {
  blockingRuleOption: CustomBlockingRulesOption;
};

export type PublisherInventoryRuleOptions = {
  blockingRuleOption: CustomBlockingRulesOption;
  domainTargetingOption: DomainTargetingType;
  isValid: boolean;
  publisherID: string;
};
